import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, Loader2 } from 'lucide-react';
import { useGoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Logo2 from "../assets/images/Logo2.png";
import SummaryApi from '../common/SummaryApi';
import { useDispatch } from "react-redux";
import { setUserDetails } from "../store/userSlice";
import axios from 'axios';
// import { jwtDecode } from "jwt-decode";

const Login = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();

  // Load saved email if "Remember Me" was checked
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for the field being edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Save email if remember me is checked
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', formData.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      const { data } = await axios.post(SummaryApi.loginEmail.url, {
        email: formData.email,
        password: formData.password
      });

      // Check if user has owner/admin role - adjust this condition based on your user data structure
      const isOwner = data?.user?.role === 'owner' || data?.user?.role === 'admin' || data?.user?.isOwner === true;

      localStorage.setItem('ata', data.accessToken);

      //fetch login user details
      // const decoded = jwtDecode(data.accessToken);
      // const userId = decoded?.userId || decoded?.id;

      const userRes = await axios.get(SummaryApi.getMe.url, {
        headers: { Authorization: `Bearer ${data.accessToken}` },
      });

      dispatch(setUserDetails(userRes?.data?.data));
      toast.success('Login successful!');

      if (isOwner) {
        navigate(`/${data?.organization?.settings?.portalName}/dashboard/sales`);
      } else {
        navigate(`/${data?.organization?.settings?.portalName}/home?tab=my-overview`);
      }


    } catch (error) {
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error('Login failed. Please try again.');
        }
      } else if (error.request) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('An unexpected error occurred.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Google Login Button (OAuth 2.0 flow)
  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'popup',
    redirect_uri: 'postmessage', // Add this explicitly
    onSuccess: async (codeResponse) => {
      // console.log('Google OAuth response:', codeResponse);

      // Check if we got a code
      if (!codeResponse.code) {
        console.error('No authorization code received');
        toast.error('No authorization code received from Google');
        return;
      }

      setGoogleLoading(true);
      try {
        const response = await axios.post(
          SummaryApi.googleLogin.url,
          { authCode: codeResponse.code },
          {
            timeout: 30000, // 30 second timeout
          }
        );

        // console.log('Login successful:', response.data);

        localStorage.setItem('ata', response.data.accessToken);
        const isOwner = response?.data?.user?.role === 'owner' || response?.data?.user?.role === 'admin' || response?.data?.user?.isOwner === true;

        //fetch login user details
        // const decoded = jwtDecode(response.data.accessToken);
        // const userId = decoded?.userId || decoded?.id;

        const userRes = await axios.get(SummaryApi.getMe.url, {
          headers: { Authorization: `Bearer ${response.data.accessToken}` },
        });

        dispatch(setUserDetails(userRes?.data?.data));

        toast.success('Google login successful!');
        if (isOwner) {
          navigate(`/${response?.data?.organization?.settings?.portalName}/dashboard/sales`);
        } else {
          navigate(`/${response?.data?.organization?.settings?.portalName}/home?tab=my-overview`);
        }

      } catch (error) {
        console.error('Login failed:', error.response?.data || error);

        const errorMessage = error.response?.data?.message || 'Google login failed';
        toast.error(errorMessage);

        // If it's an auth code issue, suggest trying again
        if (errorMessage.includes('expired') || errorMessage.includes('already used')) {
          toast.info('Please try signing in again');
        }
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (errorResponse) => {
      console.error('Google OAuth error:', errorResponse);
      toast.error(errorResponse.error_description || 'Google login failed');
    }
  });

  // Google One Tap Login (ID Token flow)
  useGoogleOneTapLogin({
    onSuccess: async (credentialResponse) => {
      try {
        // Send idToken to backend
        const { data } = await axios.post(SummaryApi.googleLogin.url, {
          idToken: credentialResponse.credential // Send as idToken
        });

        const isOwner = data?.user?.role === 'owner' || data?.user?.role === 'admin' || data?.user?.isOwner === true;

        localStorage.setItem('ata', data.accessToken);

        //fetch login user details
        // const decoded = jwtDecode(data.accessToken);
        // const userId = decoded?.userId || decoded?.id;

        const userRes = await axios.get(SummaryApi.getMe.url, {
          headers: { Authorization: `Bearer ${data.accessToken}` },
        });

        dispatch(setUserDetails(userRes?.data?.data));

        toast.success('Google One Tap login successful!');

        if (isOwner) {
          navigate(`/${data?.organization?.settings?.portalName}/dashboard/sales`);
        } else {
          navigate(`/${data?.organization?.settings?.portalName}/home?tab=my-overview`);
        }

      } catch (error) {
        console.error('One Tap login failed', error);
        toast.error(error.response?.data?.message || 'Google One Tap login failed.');
      }
    },
    onError: () => {
      // console.log('One Tap login dismissed or failed');
    }
  });

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50">
      {/* Modern Pattern Background */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(59, 130, 246, 0.1) 35px, rgba(59, 130, 246, 0.1) 70px),
              repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(239, 68, 68, 0.1) 35px, rgba(239, 68, 68, 0.1) 70px),
              repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(34, 197, 94, 0.1) 50px, rgba(34, 197, 94, 0.1) 100px)
            `
          }}
        />
        {/* Floating Squares Pattern */}
        <div className="absolute top-20 left-20 w-24 h-24 bg-blue-500 opacity-10 rounded-lg transform rotate-45"></div>
        <div className="absolute top-40 right-40 w-32 h-32 bg-red-500 opacity-10 rounded-lg transform rotate-12"></div>
        <div className="absolute bottom-20 left-1/3 w-28 h-28 bg-green-500 opacity-10 rounded-lg transform -rotate-12"></div>
        <div className="absolute bottom-40 right-20 w-20 h-20 bg-yellow-500 opacity-10 rounded-lg transform rotate-45"></div>
      </div>

      <div className="relative flex items-center justify-center min-h-screen p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 backdrop-blur-sm">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            {/* Logo Recreation */}
            <div className="flex justify-center mb-4">
              <div className=" h-20">
                <img src={Logo2} alt="Apd Logo" className="w-auto h-20" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-red-500 mb-2">Welcome back</h1>
            <p className="text-gray-600">
              Login to your{' '}
              {/* <span className="font-bold">
                <span className="text-red-500">APD</span>
                <span className="text-gray-600">GROUP</span>
              </span>{' '} */}
              account
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="m@example.com"
                  className={`
                    w-full pl-10 pr-3 py-3 bg-white border rounded-lg text-gray-900 placeholder-gray-400
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    transition-all duration-200
                    ${errors.email ? 'border-red-300' : 'border-gray-300'}
                  `}
                  disabled={isLoading}
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <a
                  href="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-500 transition-colors"
                >
                  Forgot your password?
                </a>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  className={`
                    w-full pl-10 pr-12 py-3 bg-white border rounded-lg text-gray-900 placeholder-gray-400
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    transition-all duration-200
                    ${errors.password ? 'border-red-300' : 'border-gray-300'}
                  `}
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2"
              />
              <label htmlFor="remember" className="ml-2 text-sm text-gray-700 cursor-pointer">
                Remember me
              </label>
            </div>

            {/* Login Button - with gradient matching logo colors */}
            <button
              type="submit"
              disabled={isLoading || googleLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg font-medium 
                       hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-200 flex items-center justify-center shadow-lg"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>
          </div>

          {/* Google Login Button */}
          <div className="mt-6">
            <button
              type="button"
              onClick={() => googleLogin()}
              disabled={googleLoading || isLoading}
              className="w-full flex items-center justify-center gap-3 border-2 border-gray-300 rounded-lg py-3 px-4 
                       hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
                       disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {googleLoading ? (
                <Loader2 className="animate-spin h-5 w-5 text-gray-700" />
              ) : (
                <>
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  <span className="font-medium text-gray-700">Login with Google</span>
                </>
              )}
            </button>
          </div>

          {/* Sign Up Link */}
          {/* <div className="mt-6 text-center text-sm">
            <span className="text-gray-600">Don't have an account? </span>
            <a
              href="/sign-up"
              className="text-blue-600 hover:text-blue-500 font-medium transition-colors"
            >
              Sign up
            </a>
          </div> */}

          {/* Terms and Privacy */}
          <div className="mt-6 text-center text-xs text-gray-500">
            By clicking continue, you agree to our{' '}
            <a href="/" className="text-blue-600 hover:text-blue-500 underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/" className="text-blue-600 hover:text-blue-500 underline">
              Privacy Policy
            </a>
            .
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
